import seedrandom from 'seedrandom';

export const RANDOM_LISTING_SORT = 'random';

// Buy Now uses /s; auctions use the same route with pub_isAuction=true.
// Keep explicit sorting and distance ordering intact.
export const isRandomListingOrder = ({ sort, origin, pub_isAuction }) =>
  (!sort || sort === RANDOM_LISTING_SORT) &&
  !origin &&
  pub_isAuction !== true &&
  pub_isAuction !== 'true';

export const shuffleListings = (listings, random = Math.random) => {
  const shuffled = [...listings];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const shuffleListingPage = (response, seed, page) => ({
  ...response,
  data: {
    ...response.data,
    data: shuffleListings(response.data.data, seedrandom(`${seed}:${page}`)),
  },
});

/**
 * Sample a circular window from the eligible query results, then shuffle it.
 * Every result has the same chance of inclusion, including the final partial page.
 * This is a random window, not a uniform sample of every possible combination.
 *
 * Sharetribe has no random sort. Reuse the first page's count and fetch only the
 * pages intersecting the window: at most three requests, each limited to perPage.
 * All requests use the original Marketplace SDK and eligibility filters.
 */
export const queryRandomListings = async (sdk, params) => {
  const { perPage } = params;
  const firstResponse = await sdk.listings.query({ ...params, page: 1 });
  const { totalItems, paginationLimit } = firstResponse.data.meta;
  // Respect the API's accessible pagination range for very large result sets.
  const accessibleItems = paginationLimit
    ? Math.min(totalItems, paginationLimit * perPage)
    : totalItems;
  const count = Math.min(perPage, accessibleItems);
  const start = Math.floor(Math.random() * accessibleItems);
  const positions = Array.from({ length: count }, (_, i) => (start + i) % accessibleItems);
  const pages = [...new Set(positions.map(position => Math.floor(position / perPage) + 1))];
  const responses = new Map([[1, firstResponse]]);

  await Promise.all(
    pages
      .filter(page => page !== 1)
      .map(async page => {
        responses.set(page, await sdk.listings.query({ ...params, page }));
      })
  );

  const seenIds = new Set();
  const listings = positions
    .map(position => {
      const page = Math.floor(position / perPage) + 1;
      return responses.get(page).data.data[position % perPage];
    })
    .filter(listing => {
      // Listings may be removed between requests. Never fill holes from stale data.
      if (!listing || listing.attributes.deleted || listing.attributes.state !== 'published') {
        return false;
      }
      const id = listing.id.uuid;
      if (seenIds.has(id)) {
        return false;
      }
      seenIds.add(id);
      return true;
    });

  const included = new Map();
  responses.forEach(response => {
    (response.data.included || []).forEach(entity => {
      included.set(`${entity.type}:${entity.id.uuid}`, entity);
    });
  });

  return {
    ...firstResponse,
    data: {
      ...firstResponse.data,
      data: shuffleListings(listings),
      included: [...included.values()],
    },
  };
};
